import { useMemo } from 'react';
import { Query, useQueries, UseQueryResult } from '@tanstack/react-query';
import {
  OnboardingSectionResponse,
  OnboardingSectionStatus,
  OnboardingSectionVersion,
  OnboardingStoreType,
  OracleOnboardingSection,
  OracleOnboardingSectionKey,
  ReadyOnboardingSectionVersion
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { fetchSection } from './onboarding-api';

const POLL_INTERVAL_MS = 3000;

// Keyed per store: the two stores are separate caches.
export const onboardingSectionQueryKey = (
  company: string,
  sectionKey: OracleOnboardingSectionKey,
  store: OnboardingStoreType
): string[] => ['onboardingSection', company, sectionKey, store];

const isInFlight = (v: OnboardingSectionVersion | null | undefined): boolean =>
  v?.status === OnboardingSectionStatus.PENDING || v?.status === OnboardingSectionStatus.RUNNING;

export const OnboardingCardKind = {
  EMPTY: 'empty',
  SUMMARIZING: 'summarizing',
  FAILED: 'failed',
  HAS_CURRENT: 'has-current'
} as const;
export type OnboardingCardKindType = (typeof OnboardingCardKind)[keyof typeof OnboardingCardKind];

// One state per section, derived from its current document alone.
export type OnboardingCardState =
  | { kind: typeof OnboardingCardKind.EMPTY }
  | { kind: typeof OnboardingCardKind.SUMMARIZING; version: OnboardingSectionVersion }
  | {
      kind: typeof OnboardingCardKind.FAILED;
      message: string;
      version: OnboardingSectionVersion;
    }
  | { kind: typeof OnboardingCardKind.HAS_CURRENT; version: ReadyOnboardingSectionVersion };

interface CardVersionIds {
  documentVersionId: number;
  retryableVersionId?: number;
  // The version's own store, not the selected one. The same id names a row in both tables, so a
  // retry or a download against the other one is a different document.
  versionStore: OnboardingStoreType;
}

// Null before the first upload: a version row is written only once an upload is committed.
export const versionIdsOf = (state?: OnboardingCardState): CardVersionIds | null => {
  if (!state || state.kind === OnboardingCardKind.EMPTY) return null;
  const documentVersionId = state.version.id;
  return {
    documentVersionId,
    retryableVersionId: state.kind === OnboardingCardKind.FAILED ? documentVersionId : undefined,
    versionStore: state.version.store
  };
};

export const deriveCardState = (
  response: OnboardingSectionResponse | undefined
): OnboardingCardState => {
  const current = response?.current;
  if (!current) return { kind: OnboardingCardKind.EMPTY };

  switch (current.status) {
    case OnboardingSectionStatus.READY:
      return { kind: OnboardingCardKind.HAS_CURRENT, version: current };
    case OnboardingSectionStatus.FAILED:
      return { kind: OnboardingCardKind.FAILED, message: current.statusMessage, version: current };
    case OnboardingSectionStatus.PENDING:
    case OnboardingSectionStatus.RUNNING:
      return { kind: OnboardingCardKind.SUMMARIZING, version: current };
    default: {
      // A status added to bpm-types has to be given a case here rather than falling through to
      // a spinner the poll never clears.
      const unhandled: never = current;
      return unhandled;
    }
  }
};

// The sections with a summarization prompt behind them -- the worker keys its prompts off
// this same const, so the two cannot drift.
const WIRED_SECTION_KEYS = Object.values(OracleOnboardingSection);

const sectionQueryOptions = (
  company: string,
  sectionKey: OracleOnboardingSectionKey,
  store: OnboardingStoreType
) => ({
  queryKey: onboardingSectionQueryKey(company, sectionKey, store),
  queryFn: () => fetchSection({ company, store, sectionKey }),
  enabled: Boolean(company),
  // OracleClientConfig surfaces this error itself.
  meta: { suppressGlobalError: true },
  refetchInterval: (query: Query<OnboardingSectionResponse>) => {
    const { data } = query.state;
    if (!data) return false;
    return isInFlight(data.current) ? POLL_INTERVAL_MS : false;
  }
});

interface OnboardingSections {
  cardStates: Record<OracleOnboardingSectionKey, OnboardingCardState>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

// Declared once so useQueries can memoize on its reference; it closes over nothing local.
const combineSections = (
  results: UseQueryResult<OnboardingSectionResponse>[]
): OnboardingSections => ({
  cardStates: Object.fromEntries(
    WIRED_SECTION_KEYS.map((sectionKey, i) => [sectionKey, deriveCardState(results[i].data)])
  ) as Record<OracleOnboardingSectionKey, OnboardingCardState>,
  isLoading: results.some(r => r.isLoading),
  isError: results.some(r => r.isError),
  error: results.find(r => r.error)?.error ?? null
});

export const useOnboardingSections = (
  company: string,
  store: OnboardingStoreType
): OnboardingSections => {
  const queries = useMemo(
    () => WIRED_SECTION_KEYS.map(sectionKey => sectionQueryOptions(company, sectionKey, store)),
    [company, store]
  );
  return useQueries({ queries, combine: combineSections });
};
